import { isNotEmpty, useForm } from '@mantine/form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  executeUpsertArtwork,
  updateGetArtwork,
} from 'src/services/apollo/artwork'
import type {
  ArtworkBaseFragment,
  Media,
} from 'src/services/apollo/generated/graphql'
import { ArtworkVisibilityType } from 'src/services/apollo/generated/graphql'
import { getUpsertArtworkTrackAttrs } from 'src/services/helpers/artworkApiParamsPacker'
import { useCreateArtworkWithTurnstile } from 'src/services/helpers/artworkUpload'
import { track } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { extractTackIds } from '~/services/helpers/tack'
import { ImageSource } from '../Common/ImageSource'
import { UIButton } from '../ui/Button'
import { UIForm } from '../ui/Form'
import type {
  ArtworkInfoFormProps,
  ArtworkInfoFormValue,
} from './ArtworkInfoForm'
import {
  ArtworkInfoForm,
  mergeArtworkInfoFormFields,
  mergeArtworkInfoFormValue,
} from './ArtworkInfoForm'

export type SubmitFormProps = {
  onSuccess?: (artwork: ArtworkBaseFragment) => void
  media?: Media
  artworkId?: string
  allowEditImage?: boolean
  allowSelectFromTask?: boolean
  initialValues?: Partial<ArtworkInfoFormValue>
} & Pick<ArtworkInfoFormProps<SubmitFormValue>, 'fields'>

export type SubmitFormValue = {
  media?: Media
} & ArtworkInfoFormValue

export const SubmitForm = ({
  onSuccess,
  media,
  artworkId,
  allowEditImage = true,
  allowSelectFromTask = false,
  initialValues,
  fields,
}: SubmitFormProps) => {
  const { t } = useTranslation(['action', 'artwork'])
  // Determine if this is creating a new artwork (not editing an existing one)
  const isCreatingNewArtwork = !artworkId

  // Turnstile for new artwork creation
  const {
    create: createArtwork,
    TurnstileWidget,
    turnstileReady,
  } = useCreateArtworkWithTurnstile()

  const form = useForm<SubmitFormValue>({
    initialValues: {
      media: media,
      ...mergeArtworkInfoFormValue(initialValues, {
        title: '',
        prompts: '',
        tags: [],
        hidePrompts: false,
        visibility: ArtworkVisibilityType.Public,
        autoPublish: true,
        description: '',
        extra: {
          externalParameters: undefined,
        },
      }),
    },

    validate: {
      media: isNotEmpty('Media is empty'),
    },
  })

  const [loading, submit] = useLoadingCallback(
    async (values: SubmitFormValue) => {
      if (!values.media) return

      const input = {
        mediaId: values.media.id,
        title: values.title,
        tags: [],
        tackIds: extractTackIds(values.tags),
        visibility: values.visibility,
        hidePrompts: values.hidePrompts,
        extra: {
          ...values.extra,
          description: values.description,
          challenge: values.challenge,
        },
      }

      try {
        let resultArtwork: ArtworkBaseFragment | null = null

        if (isCreatingNewArtwork) {
          // Creating new artwork - use oRPC with Turnstile
          resultArtwork = await createArtwork(input)
        } else {
          // Editing existing artwork - use GraphQL
          const { data } = await executeUpsertArtwork({
            id: artworkId,
            input: {
              ...input,
              visibility: values.visibility,
              prompts: values.prompts,
              hidePrompts: values.hidePrompts,
            },
          })
          resultArtwork = data?.upsertArtwork ?? null
          if (resultArtwork) {
            updateGetArtwork({ id: resultArtwork.id }, resultArtwork)
          }
        }

        if (!resultArtwork) {
          toast.error(`Submission failed!`)
          return
        }

        toast.success(`Submitted successfully!`)
        onSuccess?.(resultArtwork)
        track('upload_success', {
          ...getUpsertArtworkTrackAttrs({
            ...values,
            artworkId: artworkId,
          }),
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        toast.error(`Fail to submit. ${err?.message}`)
        track('upload_failed', {
          error: err?.message,
        })
      }
    },
    [isCreatingNewArtwork, artworkId, createArtwork],
  )

  // Only require turnstile for new artwork creation
  const canSubmit = !isCreatingNewArtwork || turnstileReady

  return (
    <UIForm
      form={form}
      onSubmit={submit}
      className="flex flex-col md:grid md:grid-cols-2 p-8 items-stretch gap-8"
    >
      <div className="flex flex-col gap-4 items-center">
        <ImageSource
          {...form.getInputProps('media')}
          className="w-full h-auto md:w-full md:h-auto aspect-square"
          features={{
            buttons: true,
            uploadButton: allowEditImage,
            nativeGallery: false,
            nativeCamera: false,
            taskPicker: allowSelectFromTask,
          }}
          disabled={!allowEditImage}
        />
      </div>

      <div className="flex-1 flex flex-col gap-4">
        <ArtworkInfoForm
          form={form}
          mediaId={form.getValues().media?.id}
          fields={mergeArtworkInfoFormFields(fields, {
            prompts: false,
          })}
          isEditingArtwork={!!artworkId}
        />

        {/* Turnstile verification for new artwork creation */}
        {isCreatingNewArtwork && (
          <div className="w-full">
            <TurnstileWidget />
          </div>
        )}

        <UIButton
          busy={loading}
          disabled={!canSubmit}
          variant="filled"
          size="large"
          type="submit"
        >
          {t('artwork:generation.upload-submit')}
        </UIButton>
      </div>
    </UIForm>
  )
}
